import { useModalState } from "@clipboard-health/ui-components";
import { getTheme } from "@clipboard-health/ui-theme";
import { ThemeProvider } from "@mui/material";
import { useCurrentDate } from "@src/appV2/lib/utils/useCurrentDate";
import { EmergencyCreditsEducationContainer } from "@src/appV2/redesign/ClipboardScore/components/EmergencyCreditsEducation/EmergencyCreditsEducationContainer";
import { PageWrapper } from "@src/appV2/redesign/components/PageWrapper";
import { useEmergencyCreditsConfig } from "@src/appV2/redesign/EmergencyCredits/hooks/useEmergencyCreditsConfig";
import { useCallback } from "react";
import { useHistory, useParams } from "react-router-dom";

import { CancelShiftPageContent } from "./CancelShiftPageContent";
import { useCancelShiftPageData } from "./hooks/useCancelShiftPageData";

export function CancelShiftPage(): JSX.Element {
  const { shiftId } = useParams<{ shiftId: string }>();
  const history = useHistory();
  const currentDate = useCurrentDate(1000);

  const emergencyCreditsEducationModal = useModalState();
  const emergencyCreditsConfig = useEmergencyCreditsConfig();

  const cancelShiftPageData = useCancelShiftPageData({
    shiftId,
    currentDate,
  });

  /*
   * This component is designed around emergency credits, and should only be invoked when the EC feature is enabled.
   * To avoid errors, it will gracefully degrade in the event that `emergencyCreditStats` is undefined. The resulting
   * behavior should be similar to the the standard bottom-sheet cancel flow, but has not been rigorously designed or tested.
   */

  const handleKeepShift = useCallback(() => {
    history.goBack();
  }, [history]);

  const handlePressAndHoldToCancel = useCallback(() => {
    // TODO: Implement press and hold functionality in separate ticket
    // For now, this button is shown but does not trigger cancellation
  }, []);

  const handlePreviewCredit = useCallback(() => {
    // MX-5838: Placeholder - does nothing for now
    // Future: Navigate to preview screen or trigger animation
  }, []);

  const handleWhyCantIUseCredit = useCallback(() => {
    // Display the EC education carousel for "Why can't I use my cancellation credit?"
    emergencyCreditsEducationModal.openModal();
  }, [emergencyCreditsEducationModal]);

  return (
    <ThemeProvider theme={getTheme()}>
      <PageWrapper variant="tertiary">
        <CancelShiftPageContent
          {...cancelShiftPageData}
          onKeepShift={handleKeepShift}
          onPressAndHoldToCancel={handlePressAndHoldToCancel}
          onPreviewCredit={handlePreviewCredit}
          onWhyCantIUseCredit={handleWhyCantIUseCredit}
        />

        {emergencyCreditsConfig.enabled && (
          <EmergencyCreditsEducationContainer
            showCloseButton
            modalState={emergencyCreditsEducationModal}
            markAsSeen={() => {
              // No-op: This is for informational access, not onboarding
            }}
            emergencyCreditsConfig={emergencyCreditsConfig}
          />
        )}
      </PageWrapper>
    </ThemeProvider>
  );
}
